import useLanguageStore from '@/stores/useLanguageStore';
import { convertToArabicNumbers } from '@/utils/helpers/numberHelpers';

type TFn = (key: string) => string;

/**
 * Disbursement channel slugs → human labels (QA defect 8). FFC emits raw
 * slugs (`zaincash` / `zain_cash` / `qi_card` / `idb`); normalize and map to
 * the `advance_channel.*` i18n keys. Brand names stay Latin in AR too
 * (DESIGN §6). Unknown slugs fall through verbatim rather than hiding data.
 */
const CHANNEL_KEYS: Record<string, string> = {
  zaincash: 'zaincash',
  qicard: 'qicard',
  idb: 'idb',
};

export const disbursementChannelLabel = (t: TFn, slug?: string | null): string | null => {
  if (!slug) return null;
  const normalized = slug.toLowerCase().replace(/[^a-z0-9]/g, '');
  const key = CHANNEL_KEYS[normalized];
  return key ? String(t(`advance_channel.${key}`)) : slug;
};

/**
 * failed-batch failure_reason → merchant-readable copy (QA defect 16). The
 * backend stores `CODE: technical message` (SubmitAdvanceBatchJob) — map the
 * known codes to friendly i18n strings, generic fallback for everything
 * else. The raw reason never reaches the merchant UI.
 */
const KNOWN_FAILURE_CODES = new Set([
  'insufficient_credit',
  'merchant_suspended',
  'merchant_not_found',
  'financing_not_active_at_submit',
]);

export const merchantFailureReason = (t: TFn, reason?: string | null): string => {
  const code = (reason ?? '').split(':')[0].trim().toLowerCase();
  const key = KNOWN_FAILURE_CODES.has(code)
    ? `advance_cod_failure.${code}`
    : 'advance_cod_failure.generic';
  return String(t(key));
};

/**
 * Rejected-batch reason → localized copy (item 4a). The backend sends a stable
 * `rejection_reason_code` slug alongside the friendly-mapped `rejection_reason`
 * string. Known slugs localize from `advance_cod_rejection.*`; `other` or any
 * unrecognised slug falls back to the raw `rejection_reason` string (never a
 * raw code), and the friendly failure-reason mapping is the last resort when
 * no code arrives at all (older payloads).
 */
const KNOWN_REJECTION_CODES = new Set([
  'insufficient_credit',
  'merchant_suspended',
  'merchant_not_found',
]);

export const rejectionReasonLabel = (
  t: TFn,
  code?: string | null,
  rawReason?: string | null,
  failureReason?: string | null,
): string => {
  const slug = (code ?? '').trim().toLowerCase();

  if (KNOWN_REJECTION_CODES.has(slug)) {
    return String(t(`advance_cod_rejection.${slug}`));
  }

  // `other` or an unknown slug → the raw friendly reason if the backend
  // provided one, else the generic localized `other` copy.
  if (slug) {
    return rawReason && rawReason.length > 0 ? rawReason : String(t('advance_cod_rejection.other'));
  }

  // No code at all → keep the pre-item-4a behavior (raw friendly reason, then
  // the mapped failure reason) so nothing regresses on legacy payloads.
  return rawReason && rawReason.length > 0 ? rawReason : merchantFailureReason(t, failureReason);
};

/**
 * Digit-style localizer for standalone UI numerals (QA defect 11 — stepper
 * step numbers): Arabic-Indic digits in AR, Western in EN — the exact
 * convention `formatDateByDayjs` already applies to dates. Money amounts
 * deliberately stay Western (house `formatWithThousandSeparator` rule).
 */
export const localizeDigits = (value: string | number): string => {
  const str = String(value);
  return useLanguageStore.getState().language === 'en' ? str : convertToArabicNumbers(str);
};
